import format from 'date-fns/format';
import { enUS, ja } from 'date-fns/locale';
import { Dispatch, useEffect, useRef, useState } from 'react';
import { DeepPartial } from 'redux';
import i18n from '../i18n';

declare const pendo: any;

const AMERICAN_DATE_FORMAT = 'MMMM dd, yyyy';
const JAPANESE_DATE_FORMAT = 'yoMMMdo';
const DATE_TIME_FORMAT = 'MMMM dd, yyyy, HH:mm';
const JAPANESE_DATE_TIME_FORMAT = 'yoMMMdo, HH:mm';

export const formatDate = (date: string | Date, locale: string): string => {
  return locale === 'ja'
    ? format(new Date(date), JAPANESE_DATE_FORMAT, { locale: ja })
    : format(new Date(date), AMERICAN_DATE_FORMAT, { locale: enUS });
};

export const formatDateTime = (date: string | Date, locale: string): string => {
  return locale === 'ja'
    ? format(new Date(date), JAPANESE_DATE_TIME_FORMAT, { locale: ja })
    : format(new Date(date), DATE_TIME_FORMAT, { locale: enUS });
};

/**
 * Handler for sending custom events to Pendo
 * @param eventName
 * @param params
 */
export const sendEventTrack = (eventName, params) => {
  try {
    if (typeof window['pendo'] !== 'undefined' && eventName) {
      pendo?.track(eventName, params);
    }
  } catch (e) {
    console.error('Pendo error: ', e);
  }
};

export const getResourceTypeName = (resourceType: string | undefined, plural = false) => {
  if (!resourceType) return '';
  return i18n.t(`enums.awsResource.${resourceType}`, { count: plural ? 2 : 1 });
};

export const openURL = (url: string, openInNewTab = false) => {
  const newWindow = window.open(url, openInNewTab ? '_blank' : '_self', 'noopener,noreferrer');
  if (newWindow?.opener) newWindow.opener = null; // maintains backwards compatibility for browsers that don't support "noopener" above
};

export const getCloudProviderDisplayName = (provider: 'aws' | 'azure' | 'gcp' | undefined) => {
  switch (provider) {
    case 'aws':
      return 'AWS';
    case 'azure':
      return 'Azure';
    case 'gcp':
      return 'GCP';
    default:
      return provider;
  }
};

export const getProjectDisplayName = (project: ProjectsAPI.Schemas.Project | undefined) => {
  if (!project) return '';

  if (project.alias.includes(project.cloudAccountID)) return project.alias;

  return project.alias
    ? `${project.alias} - ${project.cloudAccountID}`
    : project.cloudProvider && project.cloudAccountID
    ? `${getCloudProviderDisplayName(project.cloudProvider)} - ${project.cloudAccountID}`
    : '';
};

export const getCloudAccountDisplayName = (project: ProjectsAPI.Schemas.Project | undefined) => {
  if (!project) return '';

  if (project.alias.includes(project.cloudAccountID)) return project.alias;

  return project.alias ? `${project.alias} - ${project.cloudAccountID}` : project.cloudAccountID;
};

export const useStorageState = <T = unknown>(
  storageType: 'sessionStorage' | 'localStorage',
  key: string,
  defaultValue: DeepPartial<T> | (() => DeepPartial<T>),
  { serialize = JSON.stringify, deserialize = JSON.parse } = {}
): [DeepPartial<T>, Dispatch<DeepPartial<T>>] => {
  const storage = window[storageType];
  const [state, setState] = useState<DeepPartial<T>>(() => {
    const valueInStorage = storage.getItem(key);
    if (valueInStorage) {
      // the try/catch is here in case the storage value was set before
      // we had the serialization in place (like we do in previous extra credits)
      try {
        return deserialize(valueInStorage);
      } catch (error) {
        console.warn(`Threw error while parsing value from ${storage} (key=${key})`);
        storage.removeItem(key);
      }
    }
    return typeof defaultValue === 'function' ? defaultValue() : defaultValue;
  });

  const prevKeyRef = useRef(key);

  useEffect(() => {
    const prevKey = prevKeyRef.current;
    if (prevKey !== key) {
      storage.removeItem(prevKey);
    }
    prevKeyRef.current = key;
    storage.setItem(key, serialize(state));
  }, [key, state, serialize]);

  return [state, setState];
};

export const hasProdLogsEnabled = JSON.parse(
  localStorage.getItem('C1C_PROD_LOGS_ENABLED') || 'false'
) as boolean;
